<template>
  <router-view />
</template>

<script>
import { useInscricoesStore } from './stores/inscricoes'

export default {
  name: 'App',
  inject: ['pusher'],
  data() { return { channel: null } },
  mounted() {
    this.channel = this.pusher.subscribe('inscricoes')
    this.channel.bind('pagamento-atualizado', (payload) => {
      if (this.$route.meta?.public) return
      useInscricoesStore().applyPusherUpdate(payload)
      const tipo = payload.statusPagamento === 'QUITADA' ? 'success' :
        (payload.statusPagamento === 'PARCIAL' ? 'info' : 'warning')
      this.$toast.open({
        message: `Pagamento atualizado: ${payload.statusPagamento}`,
        type: tipo, position: 'top-right', duration: 4000
      })
    })
  }
}
</script>

<style>
:root {
  --color-text-primary: #1a1a2e;
  --color-text-secondary: #555;
  --color-text-muted: #888;
  --color-text-heading: #333;
  --color-accent: #2563eb;
  --color-accent-dark: #1e40af;
  --color-danger: #dc2626;
  --color-warning: #f59e0b;
  --color-success: #16a34a;
  --color-bg-body: #f3f4f6;
  --color-bg-surface: #ffffff;
  --color-bg-hover: #f9fafb;
  --color-bg-separator: #e5e7eb;
  --color-bg-input: #ffffff;
  --modal-overlay: rgba(15, 23, 42, 0.7);
  --font-primary: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
  --text-xs: 12px;
  --text-sm: 14px;
  --text-base: 15px;
  --text-md: 16px;
  --text-lg: 18px;
  --text-xl: 22px;
  --text-2xl: 28px;
  --text-3xl: 32px;
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 12px;
  --spacing-base: 16px;
  --spacing-lg: 24px;
  --spacing-xl: 40px;
  --shadow-card: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  --shadow-lg: 0 10px 20px rgba(0,0,0,0.08);
  --shadow-button: 0 1px 2px rgba(0,0,0,0.05);
  --radius-sm: 4px;
  --radius-md: 6px;
  --radius-lg: 10px;
  --radius-xl: 14px;
  --transition-default: all 0.2s ease-in-out;
}

* { margin: 0; padding: 0; box-sizing: border-box; touch-action: manipulation; }
body {
  background-color: var(--color-bg-body);
  font-family: var(--font-primary);
  color: var(--color-text-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
#app { min-height: 100vh; }
button { font-family: inherit; }

.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 18px; background: var(--color-accent); color: #fff;
  border: 1px solid var(--color-accent); border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 600; cursor: pointer;
  transition: var(--transition-default); box-shadow: var(--shadow-button);
}
.btn-primary:hover { background: var(--color-accent-dark); border-color: var(--color-accent-dark); }
.btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

.btn-secondary {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 18px; background: #fff; color: var(--color-accent);
  border: 1px solid var(--color-accent); border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 600; cursor: pointer;
  transition: var(--transition-default);
}
.btn-secondary:hover { background: var(--color-accent); color: #fff; }

.btn-danger {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 18px; background: var(--color-danger); color: #fff;
  border: 1px solid var(--color-danger); border-radius: var(--radius-md);
  font-size: var(--text-sm); font-weight: 600; cursor: pointer;
}

.card {
  background: var(--color-bg-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.input, .select, .textarea {
  width: 100%; padding: 10px 12px;
  border: 1px solid var(--color-bg-separator); border-radius: var(--radius-md);
  font-size: var(--text-sm); font-family: inherit;
  color: var(--color-text-primary); background: var(--color-bg-input);
  outline: none; transition: var(--transition-default);
}
.input:focus, .select:focus, .textarea:focus {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.label {
  display: block; font-size: var(--text-xs); font-weight: 600;
  color: var(--color-text-heading); margin-bottom: var(--spacing-xs);
  text-transform: uppercase; letter-spacing: 0.4px;
}

.error-msg { color: var(--color-danger); font-size: var(--text-xs); margin-top: 4px; }
.help-text { color: var(--color-text-muted); font-size: var(--text-xs); }

.page-title {
  font-size: var(--text-xl); font-weight: 700;
  color: var(--color-text-primary); margin-bottom: var(--spacing-lg);
}

.modal-overlay {
  position: fixed; inset: 0; background: var(--modal-overlay);
  display: flex; align-items: center; justify-content: center; z-index: 100;
  padding: var(--spacing-base);
}
.modal-card {
  background: var(--color-bg-surface); border-radius: var(--radius-lg);
  padding: var(--spacing-lg); width: 100%; max-width: 560px;
  max-height: 92vh; overflow-y: auto; box-shadow: var(--shadow-lg);
}
.modal-title {
  font-size: var(--text-lg); font-weight: 700;
  margin-bottom: var(--spacing-base);
}
.modal-actions {
  display: flex; gap: var(--spacing-sm); justify-content: flex-end;
  margin-top: var(--spacing-lg);
}

/* ── Responsividade ─────────────────────────────────────── */
.table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 calc(var(--spacing-base) * -1);
  padding: 0 var(--spacing-base);
}
.table-wrap table { min-width: 520px; }
.card table:not(.table-wrap table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 640px) {
  .page-title { font-size: var(--text-lg); margin-bottom: var(--spacing-md); }
  .modal-card { padding: var(--spacing-base); border-radius: var(--radius-md); }
  .modal-actions { flex-wrap: wrap; }
  .modal-actions .btn-primary, .modal-actions .btn-secondary, .modal-actions .btn-danger { flex: 1 1 auto; }
  .card { border-radius: var(--radius-md); }
}
@media (max-width: 480px) {
  :root {
    --spacing-lg: 16px;
    --spacing-xl: 24px;
  }
}
</style>
